export const en = {
  companySettings: {
    title: 'Company Settings',
    subtitle: "Personalize your company's settings",
    menu: {
      general: 'Personalization',
      administration: "Administration",
      regions: 'Company Regions',
      departments: 'Departments',
      polling: 'Polling',
      slack: 'Slack',
      microsoftTeams: 'Microsoft Teams',
      donations: 'Donations',
    },
    generalSettings: {
      title: 'Company Personalization',
      pageTitle: 'Settings › Personalization',
      logo: {
        label: 'Company Logo',
        hint: 'Uploading your company logo will add it to different parts of the site for {{companyName}} employees to see. Recommended dimensions are 200x60 pixels.',
      },
      badge: {
        label: 'Company Badge',
        hint: 'Uploading your company badge will add it to different parts of the site for {{companyName}} employees to see. The badge is meant to be a smaller, square image. Recommended dimensions are 40x40 pixels.',
      },
      color: {
        label: 'Company Colour',
        hint: "You can personalize Purposely further by adding your company's primary colour.",
      },
      saveButton: 'Save',
      successMessage: 'Company settings updated successfully',
    },
    administrationSettings: {
      title: 'Administration Settings',
      pageTitle: 'Settings › Administration',
      requireHourApprovalLabel: 'Require manual approval for submitted hours',
      successMessage: 'Administration settings updated successfully',
      requireHourApprovalHint:
        'When disabled, submitted hours will be automatically approved without manual review.',
    },
    departments: {
      title: 'Departments',
      pageTitle: 'Settings › Departments',
      subtext: "Manage your company's departments",
      table: {
        columns: {
          name: 'Name',
        },
        actions: {
          remove: 'Remove',
          activate: 'Activate',
          rename: 'Rename',
        },
        emptyState: {
          title: 'No Departments yet',
          description: "Looks like you haven't added any departments yet.",
        },
      },
    },
    departmentForm: {
      validation: {
        required: 'Required',
      },
      placeholder: 'Add a new department',
      button: 'Add department',
      successMessage: 'Saved',
    },
    confirmChangeDepartment: {
      removeTitle: 'Remove Department',
      activateTitle: 'Activate Department',
      removeConfirm:
        'Are you sure you want to remove this department? All Employees assigned to this department will need to register for a different department.',
      activateConfirm: 'Are you sure you want to activate this department?',
      buttons: {
        confirm: 'Yes',
        cancel: 'No',
      },
    },
    renameDepartment: {
      title: 'Rename Department',
      description:
        'Please note that this department will be renamed for anyone assigned to it.',
      placeholder: 'Input name',
      validation: {
        required: 'Required',
      },
      buttons: {
        save: 'Save',
      },
      successMessage: 'Saved',
    },
    polling: {
      title: 'Polling Page',
      pageTitle: 'Settings › Polling',
      description:
        'Create and manage polls for your company. Use this feature to gather opinions, make decisions, and engage with your team effectively.',
      table: {
        title: "{{companyName}}'s Polls",
        columns: {
          name: 'Name',
          options: 'Options',
          votesCast: 'Votes Cast',
          startDate: 'Start Date',
          endDate: 'End Date',
        },
        actions: {
          viewResults: 'View Results',
          edit: 'Edit',
          delete: 'Delete',
        },
        headerActions: {
          createPoll: 'Create Poll',
        },
        emptyState: {
          title: 'No polls',
          description:
            "It looks like you haven't created any polls yet. To create a new poll press the 'Create Poll' button.",
        },
      },
      featureNotAvailable: {
        title: 'Polling Feature Not Available',
        description:
          'It seems that the polling feature is not enabled for your company. Please contact your administrator for more information.',
      },
      formModal: {
        titles: {
          create: 'Create Poll',
          edit: 'Edit Poll',
        },
        fields: {
          name: {
            label: 'Name',
          },
          description: {
            label: 'Description',
          },
          startDate: {
            label: 'Poll Start Date',
          },
          endDate: {
            label: 'Poll End Date',
          },
        },
        buttons: {
          create: 'Create Poll',
          update: 'Update Poll',
        },
        validation: {
          required: 'Required',
          minOptions: 'At least two options are required',
          minOptionsForPoll: 'At least two options are required for a poll',
        },
        alerts: {
          created: 'Poll created successfully',
          updated: 'Poll updated successfully',
        },
      },
    },
    slack: {
      title: 'Slack Settings',
      pageTitle: 'Settings › Slack',
      description:
        "Manage your company's preferences for the Purposely Slack Bot",
      channelSubscription: {
        label: 'Choose a public channel',
        hint: 'This channel is where the purposely Slack Bot will send messages',
        updateButton: 'Update',
        successMessage: 'Updated Slack channel subscription',
        validation: {
          required: 'Required',
        },
      },
      onboarding: {
        title: 'Purposely on Slack',
        description: `Get updates when new volunteer events are posted, details change, teammates join in, and events wrap up— so you're always in the loop.`,
      },
    },
    slackPreferences: {
      title: 'Notification Preferences',
      description:
        'Customize which messages the Purposely Slack bot should send',
      notifications: {
        opportunityCreated: 'New opportunity created',
        opportunityCompleted: 'Opportunity completed',
        opportunityUpdated: 'Opportunity details updated',
        employeeJoined: 'Employee joined an opportunity',
        employeeDm: 'Direct messages from Purposely Slack bot',
      },
      updateButton: 'Update',
      successMessage: 'Updated Slack preferences',
    },
    microsoftTeams: {
      title: 'Teams Settings',
      pageTitle: 'Settings › Teams',
      description:
        "Manage your company's preferences for the Purposely Teams Bot",
      onboarding: {
        title: 'Purposely on Teams',
        description: `Get updates when new volunteer events are posted, details change, teammates join in, and events wrap up— so you're always in the loop.`,
      },
    },
    microsoftTeamsAuth: {
      buttonText: 'Add to Microsoft Teams',
      hint: '*It looks like you did not log in to Purposely with your Microsoft account. Please log in again with Microsoft SSO so we can link your company to Microsoft Teams.',
      alt: 'Add bot to Teams',
    },
    microsoftTeamsPreferences: {
      title: 'Notification Preferences',
      description:
        'Customize which messages the Purposely Teams bot should send',
      notifications: {
        opportunityCreated: 'New opportunity created',
        opportunityCompleted: 'Opportunity completed',
        opportunityUpdated: 'Opportunity details updated',
        employeeJoined: 'Employee joined an opportunity',
      },
      updateButton: 'Update',
      successMessage: 'Updated Teams preferences',
    },
    donations: {
      title: 'Donations',
      pageTitle: 'Settings › Donations',
      description: 'Configure donation matching and related program settings for your company.',
      successMessage: 'Donation settings updated successfully',
      matching: {
        sectionTitle: 'Employee Donation Matching',
        enabledLabel: 'Enable donation matching',
        enabledHint: 'When enabled, your company will match employee donations according to the ratio and cap configured below.',
        ratioLabel: 'Match ratio',
        ratioHint: 'e.g. 1.00 = 1:1 match, 0.50 = 50¢ per $1 donated',
        capLabel: 'Annual cap per employee ($)',
        capHint: 'Maximum match per employee per calendar year.',
      },
      volunteerReward: {
        sectionTitle: 'Dollars for Doers',
        successMessage: 'Dollars for Doers settings updated successfully',
        enabledLabel: 'Enable Dollars for Doers',
        enabledHint: 'When enabled, employees earn reward dollars for volunteer hours logged, up to the annual cap configured below.',
        rewardRateLabel: 'Reward rate ($/hour)',
        rewardRateHint: 'Dollar amount awarded per hour of approved volunteer time.',
        capLabel: 'Annual cap per employee ($)',
        capHint: 'Maximum reward payout per employee per calendar year.',
        minimumHoursLabel: 'Minimum hours per entry',
        minimumHoursHint: 'Optional. Leave blank to allow any amount. Entries below this threshold will not generate a reward.',
        requireApprovalLabel: 'Require admin approval',
        requireApprovalHint: 'When enabled, a company admin must approve volunteer logs before reward dollars are allocated.',
      },
    },
    regions: {
      title: 'Company Regions',
      pageTitle: 'Settings › Company Regions',
      subtext: "Manage your company's regions",
      table: {
        columns: {
          name: 'Name',
          type: 'Type',
        },
        actions: {
          remove: 'Remove',
        },
        emptyState: {
          title: 'No Regions yet',
          description: "Looks like you haven't added any regions yet.",
        },
      },
    },
    regionForm: {
      validation: {
        required: 'Required',
      },
      placeholder: 'Add a new region',
      button: 'Add region',
      successMessage: 'Saved',
    },
  },
}
